import { useEffect } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  useLaunchScenario,
  usePreviewScenario,
  type Scenario,
} from 'hooks/scenarios/scenarioHooks';

type Props = {
  scenario: Scenario | null;
  /** Attaches the launch to an incident, if one is in context. */
  incidentId?: string | null;
  /** Attaches the launch to an org event, if one is in context. */
  orgEventId?: string | null;
  onClose: () => void;
};

/**
 * Confirms a launch by showing what it will do.
 *
 * A scenario fires a mass notification and creates real tasks in one click, so
 * the audience count and the task list are resolved live and shown before the
 * operator commits. The count comes from the server rather than from the saved
 * config, because an area audience depends on coverage that may have changed
 * since the scenario was written.
 */
export const LaunchScenarioDialog: React.FC<Props> = ({
  scenario,
  incidentId,
  orgEventId,
  onClose,
}) => {
  const preview = usePreviewScenario();
  const launch = useLaunchScenario();

  useEffect(() => {
    if (!scenario) return;
    preview.mutate({ id: scenario.id });
    // A new preview is only wanted when the scenario being launched changes;
    // including the mutation would re-request on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scenario?.id]);

  if (!scenario) return null;

  const result = preview.data;

  return (
    <Dialog open onOpenChange={(open) => (!open ? onClose() : undefined)}>
      <DialogContent className="dark tw:bg-background tw:text-foreground">
        <DialogHeader>
          <DialogTitle>Launch “{scenario.name}”?</DialogTitle>
        </DialogHeader>

        {preview.isPending ? (
          <p className="tw:m-0 tw:text-sm tw:text-muted-foreground">
            Working out who this reaches…
          </p>
        ) : preview.isError ? (
          <p className="tw:m-0 tw:text-sm tw:text-destructive">
            Could not work out who this reaches. Launching now would send blind.
          </p>
        ) : result ? (
          <div className="tw:space-y-3">
            <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
              <Badge
                variant={result.emptyAudience ? 'destructive' : 'secondary'}
              >
                {result.recipientCount === 1
                  ? '1 recipient'
                  : `${result.recipientCount} recipients`}
              </Badge>
              {result.aorNames.length > 0 ? (
                <span className="tw:text-xs tw:text-muted-foreground">
                  {result.aorNames.join(', ')}
                </span>
              ) : null}
            </div>

            {result.emptyAudience ? (
              <p className="tw:m-0 tw:text-xs tw:text-destructive">
                Nobody matches this audience, so no notification will be sent.
                Any checklist tasks below would still be created.
              </p>
            ) : null}

            {result.actionLabels.length > 0 ? (
              <div>
                <p className="tw:m-0 tw:text-xs tw:font-medium">
                  Tasks that will be created
                </p>
                <ul className="tw:mt-1 tw:mb-0 tw:space-y-0.5 tw:pl-4 tw:text-xs tw:text-muted-foreground">
                  {result.actionLabels.map((label) => (
                    <li key={label}>{label}</li>
                  ))}
                </ul>
              </div>
            ) : (
              <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">
                No tasks will be created.
              </p>
            )}
          </div>
        ) : null}

        <DialogFooter>
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={launch.isPending || preview.isPending}
            onClick={async () => {
              await launch.mutateAsync({
                id: scenario.id,
                incidentId,
                orgEventId,
              });
              onClose();
            }}
          >
            Launch
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default LaunchScenarioDialog;
